import { useState } from 'react';
import type { Trace } from '@shared/trace';
import { useStore } from '../state/store';
import type { ScenarioSummary } from '../api';
import { Badge, Button, Card, CardBody, EmptyState, formatTime } from '../design/primitives';

/**
 * The demo's front door.
 *
 * A visitor arriving cold and a presenter running this on a screen need the same
 * two things: one sentence explaining what they are looking at, and a list of
 * things to click that each prove something specific. Everything else on this
 * page is secondary to that list.
 */
export function Overview() {
  const { bootstrap, scenarios, runScenario, activeScenario, traces } = useStore();
  const [running, setRunning] = useState<string>();

  if (!bootstrap) return null;
  const { summary } = bootstrap.estate;

  const run = async (scenarioId: string) => {
    setRunning(scenarioId);
    try {
      await runScenario(scenarioId);
    } finally {
      setRunning(undefined);
    }
  };

  return (
    <div className="mb-page">
      <Card>
        <CardBody>
          <h2 className="mb-page-title">Meridian Bank runs AI agents across its whole estate</h2>
          <p className="mb-lede">
            {summary.mcpServers} MCP servers, {summary.tools} tools, {summary.hosts} machines, three delivery pipelines
            and two cloud accounts — all driven by AI agents that can move money, restart production services and read
            customer records.
          </p>
          <p className="mb-lede">
            Every one of those actions goes through FullCourtDefense first. Run anything below and watch the decision
            path on the right: what was asked, what it actually resolved to, which rule applied, and whether it was
            allowed to happen.
          </p>

          <div className="mb-stat-row">
            <Stat label="Machines enrolled" value={summary.hosts} />
            <Stat label="MCP servers" value={summary.mcpServers} />
            <Stat label="Governed tools" value={summary.tools} />
            <Stat
              label="Servers not behind the gateway"
              value={summary.unproxiedServers}
              tone={summary.unproxiedServers > 0 ? 'warn' : 'ok'}
            />
            <Stat
              label="Credential files on disk"
              value={summary.openSecrets}
              tone={summary.openSecrets > 0 ? 'warn' : 'ok'}
            />
          </div>
        </CardBody>
      </Card>

      <section>
        <h2 className="mb-section-title">Guided scenarios</h2>
        <p className="mb-section-sub">
          Each one runs a real bank operation and proves a specific capability. Start at the top — the first is
          deliberately boring, so the ones after it have something to contrast against.
        </p>

        <div className="mb-scenario-grid">
          {scenarios.map((scenario, index) => (
            <ScenarioCard
              key={scenario.id}
              index={index + 1}
              scenario={scenario}
              busy={running === scenario.id}
              disabled={Boolean(running)}
              onRun={() => run(scenario.id)}
            />
          ))}
        </div>
      </section>

      {activeScenario && activeScenario.talkTrack.length > 0 && (
        <Card>
          <CardBody>
            <h2 className="mb-section-title">Talk track — {activeScenario.title}</h2>
            <ol className="mb-talk-track">
              {activeScenario.talkTrack.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ol>
          </CardBody>
        </Card>
      )}

      <section>
        <h2 className="mb-section-title">Recent actions</h2>
        {traces.length === 0 ? (
          <EmptyState>
            Nothing has run yet. Start a scenario above, or go to any area of the bank and perform an operation
            directly.
          </EmptyState>
        ) : (
          <div className="mb-history">
            {traces.slice(0, 12).map(trace => (
              <HistoryRow key={trace.id} trace={trace} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const OUTCOME_LABEL: Record<string, string> = {
  allowed: 'Runs normally',
  blocked: 'Will be refused',
  awaiting_approval: 'Will be held',
  mixed: 'Allowed, then refused',
};

function ScenarioCard({
  index,
  scenario,
  busy,
  disabled,
  onRun,
}: {
  index: number;
  scenario: ScenarioSummary;
  busy: boolean;
  disabled: boolean;
  onRun: () => void;
}) {
  return (
    <Card>
      <CardBody>
        <div className="mb-scenario__head">
          <span className="mb-scenario__index" aria-hidden>
            {index}
          </span>
          <h3>{scenario.title}</h3>
          <Badge tone={outcomeTone(scenario.expectedOutcome)}>
            {OUTCOME_LABEL[scenario.expectedOutcome] ?? scenario.expectedOutcome}
          </Badge>
        </div>

        <p className="mb-scenario__pitch">{scenario.pitch}</p>

        <div className="mb-scenario__block">
          <span className="mb-scenario__label">What it proves</span>
          <p>{scenario.proves}</p>
        </div>

        <div className="mb-scenario__block">
          <span className="mb-scenario__label">Verify it in</span>
          <div className="mb-scenario__surfaces">
            {scenario.surfaces.map(surface => (
              <Badge key={surface} tone="neutral" glyph={false}>
                {surface}
              </Badge>
            ))}
          </div>
        </div>

        <Button variant="primary" size="sm" onClick={onRun} disabled={disabled}>
          {busy ? 'Running…' : 'Run scenario'}
        </Button>
      </CardBody>
    </Card>
  );
}

function HistoryRow({ trace }: { trace: Trace }) {
  const { focusTrace, focusedTraceId } = useStore();
  return (
    <button
      className="mb-history__row"
      aria-current={trace.id === focusedTraceId}
      onClick={() => focusTrace(trace.id)}
    >
      <Badge tone={outcomeTone(trace.outcome)}>{(trace.outcome ?? 'running').replace('_', ' ')}</Badge>
      <span className="mb-history__title">{trace.title}</span>
      <span className="mb-history__meta">{trace.operatorName}</span>
      <span className="mb-history__meta">{formatTime(trace.startedAt)}</span>
    </button>
  );
}

function outcomeTone(outcome?: string): 'allow' | 'block' | 'approval' | 'info' | 'neutral' {
  switch (outcome) {
    case 'allowed':
    case 'approved':
      return 'allow';
    case 'blocked':
    case 'rejected':
      return 'block';
    case 'awaiting_approval':
      return 'approval';
    case 'mixed':
      return 'info';
    default:
      return 'neutral';
  }
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' }) {
  return (
    <div className={`mb-stat${tone ? ` mb-stat--${tone}` : ''}`}>
      <div className="mb-stat__value">{value}</div>
      <div className="mb-stat__label">{label}</div>
    </div>
  );
}
